import type {
  AgentId,
  BindingId,
  ChannelId,
  EntityId,
  TenantId,
} from '../core/contracts/channel-agent.js';
import type { KanbanProposal } from '../core/contracts/proposals.js';
import type { ChannelBindingRegistry } from './channel-binding-registry.js';
import type { TriggerMatcher, ChannelMessageContext } from './trigger-matcher.js';
import type { ScrumMasterWorkflow, StandupResponse } from './workflows/scrum-master-workflow.ts';
import type { KnowledgeProvider } from '../core/contracts/index.js';

export interface ChannelMessageDispatchResult {
  matched: boolean;
  bindingId?: BindingId;
  agentId?: AgentId;
  responseText?: string;
  proposals: KanbanProposal[];
}

export class ChannelAgentRouter {
  constructor(
    private bindingRegistry: ChannelBindingRegistry,
    private triggerMatcher: TriggerMatcher,
    private scrumWorkflow: ScrumMasterWorkflow,
    private knowledgeProvider?: KnowledgeProvider
  ) {}

  async dispatchChannelMessage(params: {
    tenantId: TenantId;
    channelId: ChannelId;
    senderId: EntityId;
    messageText: string;
    isReplyToAgent?: boolean;
    standupUpdate?: Omit<StandupResponse, 'submittedAt'>;
  }): Promise<ChannelMessageDispatchResult> {
    const bindings = this.bindingRegistry.getChannelBindings(params.channelId, params.tenantId);

    const context: ChannelMessageContext = {
      messageText: params.messageText,
      senderId: params.senderId,
      isReplyToAgent: params.isReplyToAgent,
    };

    for (const binding of bindings) {
      const match = this.triggerMatcher.evaluateMessageTrigger(binding, context);

      if (match.matched) {
        // Record standup if provided
        if (params.standupUpdate && binding.role === 'scrum_master') {
          this.scrumWorkflow.recordStandup(params.channelId, {
            ...params.standupUpdate,
            submittedAt: Date.now(),
          });
        }

        // Process message through ScrumMasterWorkflow
        if (binding.role === 'scrum_master') {
          const scrumResult = await this.scrumWorkflow.processScrumCommand({
            command: match.matchedToken || params.messageText,
            text: params.messageText,
            senderId: params.senderId,
            channelId: params.channelId,
            tenantId: params.tenantId,
            bindingId: binding.bindingId,
            agentId: binding.agentId,
            bindingVersion: binding.version,
          });

          return {
            matched: true,
            bindingId: binding.bindingId,
            agentId: binding.agentId,
            responseText: scrumResult.messageText,
            proposals: scrumResult.proposals,
          };
        }

        // Generic Channel Agent Response
        let resolvedKnowledge = '';
        if (this.knowledgeProvider) {
          const kRes = await this.knowledgeProvider.query({
            query: params.messageText,
            mode: binding.knowledgePolicy.mode,
            scope: {
              tenantId: params.tenantId,
              meshId: 'mesh-local',
              roomId: params.channelId,
              requesterId: params.senderId,
            },
          });
          resolvedKnowledge = kRes.content;
        }

        return {
          matched: true,
          bindingId: binding.bindingId,
          agentId: binding.agentId,
          responseText: `🤖 [${binding.role.toUpperCase()} AGENT]: Processed request "${params.messageText}".\n${resolvedKnowledge ? `Context: ${resolvedKnowledge}` : ''}`,
          proposals: [],
        };
      }
    }

    return {
      matched: false,
      proposals: [],
    };
  }
}
